(ns ncr.widgets.core
  (:require
   [clojure.core.match :refer [match]]
   [clojure.string :as str]
   [goog.events.EventType :as EventType]
   [taoensso.truss :refer-macros [have!]]
   [goog.events :as gevents]
   [lambdaisland.glogi :as log]
   [tick.core :as t]
   [uix.core.alpha :as uix]
   [ncr.etc.util :as u]
   [ncr.widgets.attributes :as attrs]
   [ncr.widgets.util :as wutil]
   [ncr.etc.pf :as pf]))

(defn merge-props
  "Utility function that merges some maps, handling `:class` and `:style`.
  The :class value is always normalized (using `class-names`) even if no
  merging is done."
  ([] (wutil/merge-props*))
  ([defaults] (wutil/merge-props* defaults))
  ([defaults props] (wutil/merge-props* defaults props))
  ([defaults props & others] (apply wutil/merge-props* defaults props others)))

(defn props [[props & _]]
  (if (map? props)
    props
    {}))

(defn children [[props & children :as all]]
  (into [:<>]
    (if (map? props)
      children all)))

(defn c-translated [tag & args]
  (let [translate identity]
    (match [args]
      [([(props :guard map?) (kw :guard keyword?) & args'] :seq)]
      [tag props (apply translate kw args')]

      [([(kw :guard keyword?) & args'] :seq)]
      [tag (apply translate kw args')]

      [([(props :guard map?) & args'] :seq)]
      (vec (concat [tag props] args'))

      [([& args'] :seq)]
      (vec (concat [tag] args')))))

(def c-span
  (partial c-translated :span))

(def c-strong
  (partial c-translated :strong))

(def c-p
  (partial c-translated :p))

(def c-h1
  (partial c-translated :h1))

#_(defn- call-or-dispatch [{:keys [on-click dispatch]} & args]
    (cond
      (fn? on-click)
      (apply on-click args)
      (coll? dispatch)
      (rf/dispatch dispatch)))

(defn c-mdi-icon [{:keys [icon] :as props}]
  [:i.mdi
   (merge-props (attrs/filter props)
     (when icon
       {:class
        (keyword (str "mdi-" (name icon)))}))])

(defn c-toolbar-icon [props]
  [c-mdi-icon
   (merge-props
     props {:class :mdi-18px})])

(defn c-menu-icon [props]
  [c-mdi-icon
   (merge-props
     props {:class :mdi-18px})])

(defn c-bare-status [message]
  [:div.pf-u-m-sm
   [:strong message]])

(defn c-empty-progress
  ([action]
   [c-empty-progress {} action])
  ([{:keys [size] :as props} action]
   [pf/c-empty-state (dissoc props :size)
    [pf/c-empty-state-icon
     {:variant :container :component [pf/c-spinner {:size size}]}]
    [pf/c-title {:heading-level :h4 :size :lg} "Loading..."]]))

(defn c-inline-error [{:keys [message title] :or {title "Application Error"}}]
  [pf/c-alert
   {:is-inline true
    :variant :danger
    :title title}
   (str message)])

(defn c-empty-error [{:keys [title body icon] :or {icon :robot-dead-outline}}]
  [pf/c-empty-state {:is-full-height false :variant :xl}
   [pf/c-empty-state-icon
    {:variant :container :component
     [c-mdi-icon {:icon icon}]}]
   (when-not (str/blank? title)
     [pf/c-title {:heading-level :h4 :size :lg} title])
   [pf/c-empty-state-body body]])

(defn c-empty-error-body
  ([code]
   (c-empty-error-body {:prefix "Unexpected Error"} code))
  ([{:keys [prefix]} code]
   [:span.pf-u-danger-color-100
    [:span (u/format "%s: " prefix)]
    [:span.pf-u-font-weight-bold (str code)]]))


(defn error-boundary [error-fn]
  (uix/create-error-boundary
    {:error->state (fn [error] [:error error])
     :handle-catch (fn [error info] (log/error :what :exception :error error :info info))}
    (fn [state [body]]
      (match @state
        [:error error]
        (error-fn error)
        _
        body))))

(def c-error-boundary
  (error-boundary
    (fn [^js error]
      [c-inline-error
       {:title "Internal component error"
        :message (.-message error)}])))

(def nbsp "\u00A0")

(defn c-with-experimental-label [{:keys [label]}]
  [:span label [:i.mdi.mdi-flask.pf-u-ml-xs.pf-u-warning-color-100]])

(defn c-toolbar-button [{:keys [tooltip toolbar-item-props experimental?] :as props}]
  (u/without [tooltip icon toolbar-item-props experimental?] props
    (let
      [btn
       [pf/c-button
        (merge-props
          {:variant :plain}
          props
          (when experimental? {:class :ncr-m-experimental}))
        (if (or (keyword? icon) (string? icon))
          [c-toolbar-icon
           {:icon icon}]
          icon)]]
      [pf/c-toolbar-item
       (or toolbar-item-props {})
       (if tooltip
         [pf/c-tooltip
          {:content
           [:span
            (if experimental?
              [c-with-experimental-label {:label tooltip}]
              tooltip)]}
          btn]
         btn)])))

(defn c-avatar [{:keys [alt src border experimental?] :as props}]
  (u/without [experimental?] props
    (if (string? src)
      [pf/c-avatar
       (cond-> props
         experimental? (assoc :class :ncr-m-experimental))]
      [:div
       (merge-props (attrs/filter props)
         {:style
          {:display :flex :align-items :center :justify-content :center}
          :class
          [:pf-c-avatar
           (case border
             :light :pf-m-light
             :dark :pf-m-dark
             nil)
           :ncr-m-experimental (or experimental? nil)]})
       (if src
         [:i {:icon src :class [:mdi (str "mdi-" (name src))]}]
         [:span.pf-u-font-size-sm {:style {:cursor :default}} alt])])))

(def default-formatter
  (t/formatter "YYYY MMM d, HH:mm"))

(defn c-date-distance
  ([date]
   (c-date-distance {} date))
  ([{:keys [formatter] :or {formatter default-formatter} :as props} date]
   (when date
     [pf/c-tooltip
      {:content
       (t/format formatter date)}
      [:span (attrs/filter props)
       (u/date-distance
         (t/date-time date))]])))

(defn c-dropzone-target [hidden?]
  [:div.ncr-c-dropzone-target
   (merge {}
     (when hidden?
       {:style {:display :none}}))])

(defn use-event [ref event-type event-handler]
  (let [element (uix/state nil)]

    (uix/with-effect nil
      (when-let [el (and (nil? @element) @ref)]
        (reset! element el)))

    (uix/with-effect []
      #(reset! element nil))

    (uix/with-effect [@element event-type event-handler]
      (when-let [el @element]
        (gevents/listen el event-type event-handler)
        #(gevents/unlisten el event-type event-handler)))))

(defn c-context-menu [{:keys [reference menu]} children]
  (let [open? (uix/state false)
        close! #(reset! open? false)]

    (use-event reference
      EventType/CONTEXTMENU
      (u/isolate* #(reset! open? true)))

    (use-event (uix/ref js/document)
      EventType/MOUSEUP
      (fn [^js e] (when (and (= 2 (.-button e)) (not (.contains ^js @reference (.-target e)))) (close!))))

    [pf/c-popover
     {:show-close false
      :class :c-context-menu
      :position :bottom-end
      :distance 0
      :should-close close!
      :enable-flip false
      :has-auto-width true
      :is-visible @open?
      :body-content (menu close!)}

     children]))


(defn- clear-timeout [*timeout]
  (when-let [^js t @*timeout] (.stop t) (.dispose t) (reset! *timeout nil)))

(defn nth-click [& handlers]
  (let [*clicks (uix/ref 0)
        *reset-clicks (uix/ref nil)
        *fire-listener (uix/ref nil)]

    {:on-mouse-down (fn [_]
                      (clear-timeout *fire-listener)
                      (reset! *reset-clicks (u/set-timeout 375 #(reset! *clicks 0)))
                      (swap! *clicks inc))

     :on-mouse-up (fn [event]
                    (clear-timeout *reset-clicks)
                    (when-let [handler (nth handlers (dec @*clicks) nil)]
                      (reset! *fire-listener
                        (u/set-timeout 200
                          (fn []
                            (handler event)
                            (reset! *clicks 0))))))}))

(defn svg [{:keys [data title]}]
  (let [^js svgDoc (.parseFromString (js/DOMParser.) data "image/svg+xml")
        ^js svg (.querySelector svgDoc "svg")]
    [:svg {:view-box (str "0 0 " (.getAttribute svg "width") " " (.getAttribute svg "height"))}
     (when title
       [:title title])
     [:path {:d (.getAttribute (.querySelector svgDoc "path") "d")}]]))
